// repo/primary-care-connect/frontend/src/pages/Portal/CommunityNavigator/CaseDetail/CaseDetailPage.tsx
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import "./CaseDetailPage.css";
import { useNavigatorDialog } from "../useNavigatorDialog";
import { portalFetch } from "../../../../utils/portalRoleChoice";
import { useRegisterIntakeUnsavedLeaveGuard } from "../intakeUnsavedLeaveGuard";
import {
  buildIntakePersistSnapshot,
  cloneIntakeFormState,
  isIntakePersistDirty,
  shouldGuardIntakeLeave,
} from "./intakeUnsavedState";

import ActivityTimeline from "./components/ActivityTimeline";
import ClosureDetailsCard from "./components/ClosureDetailsCard";
import MatchResultsSection from "./components/MatchResultsSection";
import NavigatorIntakeForm from "./components/NavigatorIntakeForm";
import type { IntakeStepNavigationRequest } from "./components/NavigatorIntakeForm";
import PatientDetailsCard from "./components/PatientDetailsCard";
import ProviderMatchingProfileModal from "./components/ProviderMatchingProfileModal";
import ProviderRequestHistory from "./components/ProviderRequestHistory";
import ProviderSubmissionCard from "./components/ProviderSubmissionCard";
import PatientPhoneFollowUpPanel from "./components/PatientPhoneFollowUpPanel";
import ActiveWaitlistsCard from "./components/ActiveWaitlistsCard";
import WorkspaceCommandCard from "./components/WorkspaceCommandCard";
import {
  ApiError,
  markCommunityNavigatorPatientContact,
} from "../../../../api/communityNavigator";

import type {
  CaseDetailResponse,
  ForwardCaseResponse,
  IntakeFormState,
  IntakeSaveResponse,
  ManualProviderSearchResult,
  MatchRunResponse,
  NavigatorCaseMatchResult,
  NavigatorPatientContactTask,
  ProviderSearchResponse,
} from "./types";
import { emptyIntakeForm } from "./intakeDefaults";
import {
  hasRecordedNavigatorConsent,
  INTAKE_BASICS_STEP_INDEX,
  isConsentRequiredForwardError,
  isNavigatorConsentAccepted,
} from "./intakeConsent";
import {
  hasCompletedNavigatorIntake,
  hasSavedDraftAwaitingCompletion,
} from "./intakeCompletionState";
import {
  countMatchedProviders,
  formatMatchRunCompletionMessage,
} from "./matchRunCompletionMessage";
import {
  formatDateOnly,
  formatPatientName,
  formatStatus,
  getWorkspaceNotice,
  toTextList,
} from "./intakeUtils";
import {
  buildGeneratedIntakeSummary,
  buildHydratedIntakeForm,
  buildIntakeAnswers,
} from "./intakeSummary";

const INTAKE_REVISION_CONFLICT_MESSAGE =
  "This intake was updated in another session. Your changes are still on this page. Refresh only after reviewing or copying your unsaved information.";

type IntakeProgressStatus = "saved" | "unsaved" | "saving" | "failed";

const CASES_LIST_PATH = "/portal/community-navigator/cases";

type CasesReturnLocationState = {
  casesReturnTo?: unknown;
  casesSearchQ?: unknown;
};

function sanitizeCasesReturnTo(raw: unknown): string | null {
  if (typeof raw !== "string") return null;

  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Reject absolute / protocol-relative / scheme URLs.
  if (trimmed.startsWith("//")) return null;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed)) return null;

  const hashIndex = trimmed.indexOf("#");
  const withoutHash = hashIndex >= 0 ? trimmed.slice(0, hashIndex) : trimmed;
  const queryIndex = withoutHash.indexOf("?");
  const pathname =
    queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;

  if (pathname !== CASES_LIST_PATH) return null;

  return withoutHash;
}

function readCasesSearchQ(state: unknown): string {
  if (!state || typeof state !== "object") return "";
  const raw = (state as CasesReturnLocationState).casesSearchQ;
  return typeof raw === "string" ? raw : "";
}

function buildCasesBackNavigation(state: unknown): {
  to: string;
  state: { casesSearchQ?: string };
} {
  const returnTo = sanitizeCasesReturnTo(
    state && typeof state === "object"
      ? (state as CasesReturnLocationState).casesReturnTo
      : null
  );

  if (!returnTo) {
    return { to: CASES_LIST_PATH, state: {} };
  }

  const trimmedQ = readCasesSearchQ(state).trim();
  return {
    to: returnTo,
    state: trimmedQ ? { casesSearchQ: trimmedQ } : {},
  };
}

const CASE_DETAIL_AREAS = [
  "patient",
  "intake",
  "matching",
  "follow-up",
] as const;

type CaseDetailArea = (typeof CASE_DETAIL_AREAS)[number];

const caseDetailAreaTabs: { area: CaseDetailArea; label: string }[] = [
  { area: "patient", label: "Patient & Contact" },
  { area: "intake", label: "Intake" },
  { area: "matching", label: "Matching" },
  { area: "follow-up", label: "Follow-up & Activity" },
];

function parseCaseDetailArea(raw: string | null): CaseDetailArea {
  const value = String(raw || "").trim().toLowerCase();
  if (
    value === "patient" ||
    value === "intake" ||
    value === "matching" ||
    value === "follow-up"
  ) {
    return value;
  }

  return "patient";
}

function getCaseAssignmentLabel(
  assignedNavigatorUserId: number | null | undefined,
  assignedNavigatorName: string | null | undefined,
  isAssignedToMe: boolean | undefined
): string {
  if (!assignedNavigatorUserId) return "Unassigned";
  if (isAssignedToMe) return "You";
  return assignedNavigatorName || "Assigned navigator";
}

function canShowAssignToMeAndStart(
  isClosed: boolean | undefined,
  isAssignedToMe: boolean | undefined,
  assignedNavigatorUserId: number | null | undefined
): boolean {
  if (isClosed !== false) return false;
  if (isAssignedToMe) return false;
  if (assignedNavigatorUserId) return false;
  return true;
}

type PatientDetailsPatchResponse = CaseDetailResponse & {
  error?: string;
};

type CloseCaseResponse = {
  ok: boolean;
  navigatorCaseId?: number;
  error?: string;
};

type PatientDetailsFormState = {
  postalCode: string;
  gender: string;
  patientCityId: string;
  email: string;
  phoneNumber: string;
  applicantEmail: string;
  applicantPhone: string;
  navigatorContactPhone: string;
};

type CityOption = {
  CityID: number;
  Name: string;
};

type PatientDetailsWorkflowFlags = {
  canEditPatientDetails?: boolean;
  patientDetailsComplete?: boolean;
  missingPatientDetails?: string[];
  hasFullPostalCode?: boolean;
  hasPatientGender?: boolean;
};

const emptyPatientDetailsForm: PatientDetailsFormState = {
  postalCode: "",
  gender: "",
  patientCityId: "",
  email: "",
  phoneNumber: "",
  applicantEmail: "",
  applicantPhone: "",
  navigatorContactPhone: "",
};

const patientGenderOptions = [
  "Female",
  "Male",
  "Non-binary",
  "Prefer not to say",
];

const manualSelectionReasonOptions = [
  "Provider specifically requested this case",
  "OHT / community pathway",
  "CHC-directed pathway",
  "Patient already known to clinic",
  "Navigator judgment",
  "No suitable recommended match",
  "Other",
];

const closeCaseReasonOptions = [
  "No suitable match",
  "Patient unreachable",
  "Patient withdrew",
  "Accepted elsewhere",
  "Administrative closure",
];

function isFullCanadianPostalCode(value: string | null | undefined): boolean {
  const compact = String(value || "")
    .trim()
    .replace(/\s+/g, "")
    .toUpperCase();

  return /^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(compact);
}

function isInternalNoEmailAddress(value: string | null | undefined): boolean {
  return String(value || "")
    .trim()
    .toLowerCase()
    .endsWith("@internal.acceptingmd.local");
}

function displayEditableEmail(value: string | null | undefined): string {
  return isInternalNoEmailAddress(value) ? "" : value || "";
}

function formatMissingPatientDetails(items: string[]): string {
  if (!items.length) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;

  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

function getWorkflowPatientDetailsFlags(
  workflow: unknown
): PatientDetailsWorkflowFlags {
  if (!workflow || typeof workflow !== "object") return {};
  return workflow as PatientDetailsWorkflowFlags;
}

function getPatientDetailsInitialForm(
  caseDetail: NonNullable<CaseDetailResponse["case"]>
): PatientDetailsFormState {
  return {
    postalCode: isFullCanadianPostalCode(caseDetail.patient.postalCode)
      ? caseDetail.patient.postalCode || ""
      : "",
    gender: caseDetail.patient.gender || caseDetail.gender || "",
    patientCityId: caseDetail.patientCityId
      ? String(caseDetail.patientCityId)
      : "",
    email: displayEditableEmail(caseDetail.patient.email),
    phoneNumber: caseDetail.patient.phoneNumber || "",
    applicantEmail: displayEditableEmail(caseDetail.patient.applicantEmail),
    applicantPhone: caseDetail.patient.applicantPhone || "",
    navigatorContactPhone: caseDetail.patient.navigatorContactPhone || "",
  };
}

function normalizeCityOptions(payload: unknown): CityOption[] {
  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as { cities?: unknown })?.cities)
      ? (payload as { cities: unknown[] }).cities
      : [];

  return rows
    .map((row) => {
      const record = row as Record<string, unknown>;
      return {
        CityID: Number(record.CityID ?? record.cityId ?? record.id),
        Name: String(record.Name ?? record.name ?? "").trim(),
      };
    })
    .filter((city) => Number.isFinite(city.CityID) && city.CityID > 0 && city.Name.length > 0)
    .sort((a, b) => a.Name.localeCompare(b.Name));
}

function getPreferredLanguageIdForSave(
  intakeForm: IntakeFormState,
  caseDetail: NonNullable<CaseDetailResponse["case"]>
): number | null {
  const selectedLanguageId = Number.parseInt(
    String(intakeForm.preferredLanguageId || ""),
    10
  );

  if (Number.isFinite(selectedLanguageId) && selectedLanguageId > 0) {
    return selectedLanguageId;
  }

  return caseDetail.preferredLanguageId || null;
}

function formatCapacity(value: number | null | undefined): string {
  if (value === null || value === undefined) return "Not declared";
  return String(value);
}

function getProviderSearchWarningText(
  provider: ManualProviderSearchResult
): string {
  if (!provider.warnings.length) return "No major warnings recorded.";
  return provider.warnings.join(" ");
}

function getManualProviderSelectLabel(
  provider: ManualProviderSearchResult,
  isSelected: boolean
): string {
  if (provider.previouslyUsedForCase || !provider.isForwardable) {
    if (provider.previouslyUsedForCase) {
      return "Previously used";
    }
    return "Unavailable";
  }

  return isSelected ? "Selected" : "Select";
}

export default function CaseDetailPage() {
  const { navigatorCaseId } = useParams<{ navigatorCaseId: string }>();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const activeArea = parseCaseDetailArea(searchParams.get("area"));
  const casesBackNavigation = useMemo(
    () => buildCasesBackNavigation(location.state),
    [location.state]
  );
  const registerIntakeLeaveGuard = useRegisterIntakeUnsavedLeaveGuard();
  const [data, setData] = useState<CaseDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [intakeForm, setIntakeForm] =
    useState<IntakeFormState>(emptyIntakeForm);
  const [isSavingIntake, setIsSavingIntake] = useState(false);
  const [intakeSaveError, setIntakeSaveError] = useState<string | null>(null);
  const [intakeSaveSuccess, setIntakeSaveSuccess] = useState<string | null>(
    null
  );
  const [intakeRevision, setIntakeRevision] = useState(0);
  const [savedIntakeBaseline, setSavedIntakeBaseline] = useState("");
  const [intakeHydrated, setIntakeHydrated] = useState(false);
  const [isSavingProgress, setIsSavingProgress] = useState(false);
  const [progressSaveError, setProgressSaveError] = useState<string | null>(
    null
  );
  const [progressInFlightSnapshot, setProgressInFlightSnapshot] = useState<
    string | null
  >(null);
  const intakeFormRef = useRef(intakeForm);
  const savedIntakeBaselineRef = useRef("");
  const savedIntakeFormRef = useRef<IntakeFormState>(
    cloneIntakeFormState(emptyIntakeForm)
  );
  const allowIntakeHydrateRef = useRef(false);
  const hydratedIntakeCaseIdRef = useRef<number | null>(null);
  const isIntakeDirtyRef = useRef(false);
  const activeAreaRef = useRef(activeArea);
  const pendingIntakeLeaveActionRef = useRef<(() => void) | null>(null);
  const [isIntakeLeaveDialogOpen, setIsIntakeLeaveDialogOpen] = useState(false);
  const intakeLeaveDialogRef = useRef<HTMLElement>(null);
  const intakeLeaveStayRef = useRef<HTMLButtonElement>(null);
  const [isConsentRequiredDialogOpen, setIsConsentRequiredDialogOpen] =
    useState(false);
  const consentRequiredDialogRef = useRef<HTMLElement>(null);
  const consentRequiredGoToBasicsRef = useRef<HTMLButtonElement>(null);
  const [isIntakeChangesSavedDialogOpen, setIsIntakeChangesSavedDialogOpen] =
    useState(false);
  const intakeChangesSavedDialogRef = useRef<HTMLElement>(null);
  const intakeChangesSavedCompleteRef = useRef<HTMLButtonElement>(null);
  const [
    pendingDraftAwaitingCompletion,
    setPendingDraftAwaitingCompletion,
  ] = useState(false);
  const [intakeStepNavigationRequest, setIntakeStepNavigationRequest] =
    useState<IntakeStepNavigationRequest | null>(null);
  const intakeStepNavigationRequestIdRef = useRef(0);
  intakeFormRef.current = intakeForm;
  activeAreaRef.current = activeArea;

  const [patientDetailsForm, setPatientDetailsForm] =
    useState<PatientDetailsFormState>(emptyPatientDetailsForm);
  const [isPatientDetailsModalOpen, setIsPatientDetailsModalOpen] =
    useState(false);
  const [isSavingPatientDetails, setIsSavingPatientDetails] = useState(false);
  const [patientDetailsSaveError, setPatientDetailsSaveError] = useState<
    string | null
  >(null);
  const [patientDetailsSaveSuccess, setPatientDetailsSaveSuccess] = useState<
    string | null
  >(null);
  const [patientCityOptions, setPatientCityOptions] = useState<CityOption[]>(
    []
  );
  const [isLoadingPatientCities, setIsLoadingPatientCities] = useState(false);
  const [patientCitiesError, setPatientCitiesError] = useState<string | null>(
    null
  );

  const [isRunningMatch, setIsRunningMatch] = useState(false);
  const [matchRunError, setMatchRunError] = useState<string | null>(null);
  const [matchRunSuccess, setMatchRunSuccess] = useState<string | null>(null);

  const [isForwardingMatch, setIsForwardingMatch] = useState(false);
  const [forwardingMatchResultId, setForwardingMatchResultId] = useState<
    number | null
  >(null);
  const [forwardError, setForwardError] = useState<string | null>(null);
  const [forwardSuccess, setForwardSuccess] = useState<string | null>(null);

  const [isManualProviderModalOpen, setIsManualProviderModalOpen] =
    useState(false);
  const [manualProviderSearchQuery, setManualProviderSearchQuery] =
    useState("");
  const [manualProviderSearchResults, setManualProviderSearchResults] =
    useState<ManualProviderSearchResult[]>([]);
  const [manualProviderSearchError, setManualProviderSearchError] = useState<
    string | null
  >(null);
  const [isSearchingManualProviders, setIsSearchingManualProviders] =
    useState(false);
  const [selectedManualProvider, setSelectedManualProvider] =
    useState<ManualProviderSearchResult | null>(null);
  const [manualSelectionReason, setManualSelectionReason] = useState("");
  const [manualSelectionNote, setManualSelectionNote] = useState("");
  const [isForwardingManualProvider, setIsForwardingManualProvider] =
    useState(false);

  const [
    selectedProviderProfileId,
    setSelectedProviderProfileId,
  ] = useState<number | null>(null);

  const [isCloseCaseModalOpen, setIsCloseCaseModalOpen] = useState(false);
  const [closeCaseReason, setCloseCaseReason] = useState("");
  const [closeCaseNote, setCloseCaseNote] = useState("");
  const [isClosingCase, setIsClosingCase] = useState(false);
  const [closeCaseError, setCloseCaseError] = useState<string | null>(null);
  const [isAssigningCase, setIsAssigningCase] = useState(false);
  const [assignCaseError, setAssignCaseError] = useState<string | null>(null);
  const [recordingPatientContactRequestId, setRecordingPatientContactRequestId] =
    useState<number | null>(null);
  const [patientContactError, setPatientContactError] = useState<string | null>(
    null
  );
  const isAssigningCaseRef = useRef(false);
  const patientDialogRef = useRef<HTMLElement>(null);
  const patientPostalRef = useRef<HTMLInputElement>(null);
  const patientGenderRef = useRef<HTMLSelectElement>(null);
  const patientErrorRef = useRef<HTMLDivElement>(null);
  const patientSuccessRef = useRef<HTMLElement>(null);
  const suppressPatientReturnFocusRef = useRef(false);
  const pendingPatientSuccessFocusRef = useRef(false);
  const manualDialogRef = useRef<HTMLElement>(null);
  const manualSearchRef = useRef<HTMLInputElement>(null);
  const manualErrorRef = useRef<HTMLDivElement>(null);
  const suppressManualReturnFocusRef = useRef(false);
  const pendingForwardSuccessFocusRef = useRef(false);
  const forwardSuccessRef = useRef<HTMLDivElement>(null);
  const closeDialogRef = useRef<HTMLElement>(null);
  const closeReasonRef = useRef<HTMLSelectElement>(null);
  const closeNoteRef = useRef<HTMLTextAreaElement>(null);
  const closeErrorRef = useRef<HTMLDivElement>(null);
  const suppressCloseReturnFocusRef = useRef(false);
  const pendingCloseHeadingFocusRef = useRef(false);
  const caseHeadingRef = useRef<HTMLHeadingElement>(null);

  const caseIdNumber = useMemo(() => {
    const parsed = Number.parseInt(String(navigatorCaseId || ""), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [navigatorCaseId]);

  const loadCaseDetail = useCallback(async () => {
    if (!caseIdNumber) {
      setIsLoading(false);
      setLoadError("Valid navigator case ID is required.");
      return false;
    }

    setIsLoading(true);
    setLoadError(null);

    try {
      const response = await portalFetch(
        `/api/portal/community-navigator/cases/${caseIdNumber}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      const payload = (await response.json()) as CaseDetailResponse;

      if (!response.ok || !payload.ok) {
        throw new Error(payload?.error || "Unable to load navigator case.");
      }

      setData(payload);
      return true;
    } catch (err) {
      setLoadError(
        err instanceof Error ? err.message : "Unable to load navigator case."
      );
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [caseIdNumber]);

  useEffect(() => {
    let isMounted = true;

    async function runLoad() {
      if (!isMounted) return;
      await loadCaseDetail();
    }

    runLoad();

    return () => {
      isMounted = false;
    };
  }, [loadCaseDetail]);

  const caseDetail = data?.case || null;
  const workflow = data?.workflow || null;
  const activity = data?.activity || [];
  const matchResults = data?.latestMatchResults || [];

  const openArea = useCallback(
    (area: CaseDetailArea) => {
      if (searchParams.get("area") === area) return;

      const next = new URLSearchParams(searchParams);
      next.set("area", area);
      setSearchParams(next, { state: location.state });
    },
    [searchParams, setSearchParams, location.state]
  );

  const goToIntakeBasicsConsent = useCallback(() => {
    intakeStepNavigationRequestIdRef.current += 1;
    setIntakeStepNavigationRequest({
      stepIndex: INTAKE_BASICS_STEP_INDEX,
      focusConsent: true,
      requestId: intakeStepNavigationRequestIdRef.current,
    });
    openArea("intake");
  }, [openArea]);

  const hasRecordedConsent = useMemo(
    () => hasRecordedNavigatorConsent(caseDetail?.intakeAnswers),
    [caseDetail?.intakeAnswers]
  );

  const workflowPatientDetailsFlags = useMemo(
    () => getWorkflowPatientDetailsFlags(workflow),
    [workflow]
  );

  const patientDetailsMissing = useMemo(() => {
    if (
      Array.isArray(workflowPatientDetailsFlags.missingPatientDetails) &&
      typeof workflowPatientDetailsFlags.patientDetailsComplete === "boolean"
    ) {
      return workflowPatientDetailsFlags.missingPatientDetails;
    }

    const missing: string[] = [];

    if (!isFullCanadianPostalCode(caseDetail?.patient.postalCode)) {
      missing.push("Full postal code");
    }

    if (!(caseDetail?.patient.gender || caseDetail?.gender)) {
      missing.push("Gender");
    }

    return missing;
  }, [
    caseDetail?.gender,
    caseDetail?.patient.gender,
    caseDetail?.patient.postalCode,
    workflowPatientDetailsFlags.missingPatientDetails,
    workflowPatientDetailsFlags.patientDetailsComplete,
  ]);

  const patientDetailsComplete = patientDetailsMissing.length === 0;
  const canEditPatientDetails = Boolean(
    workflowPatientDetailsFlags.canEditPatientDetails
  );

  useEffect(() => {
    if (!caseDetail) return;

    const caseId = caseDetail.navigatorCaseId;
    const isNewCase = hydratedIntakeCaseIdRef.current !== caseId;
    const currentSnapshot = buildIntakePersistSnapshot(intakeFormRef.current);
    const dirty = currentSnapshot !== savedIntakeBaselineRef.current;

    if (!isNewCase && !allowIntakeHydrateRef.current && dirty) {
      return;
    }

    const nextForm = buildHydratedIntakeForm(caseDetail);
    const nextSnapshot = buildIntakePersistSnapshot(nextForm);
    const nextRevision = Number.isInteger(caseDetail.intakeRevision)
      ? caseDetail.intakeRevision
      : 0;

    intakeFormRef.current = nextForm;
    savedIntakeBaselineRef.current = nextSnapshot;
    savedIntakeFormRef.current = cloneIntakeFormState(nextForm);
    hydratedIntakeCaseIdRef.current = caseId;
    allowIntakeHydrateRef.current = false;
    setIntakeForm(nextForm);
    setSavedIntakeBaseline(nextSnapshot);
    setIntakeRevision(nextRevision);
    setIntakeHydrated(true);
    setIntakeSaveError(null);
    setIntakeSaveSuccess(null);
    setProgressSaveError(null);
    setProgressInFlightSnapshot(null);
  }, [caseDetail]);

  useEffect(() => {
    if (!caseDetail) return;

    setPatientDetailsForm(getPatientDetailsInitialForm(caseDetail));
    setPatientDetailsSaveError(null);
  }, [caseDetail]);

  const priorityReasons = useMemo(
    () => toTextList(caseDetail?.priorityReasons || []),
    [caseDetail?.priorityReasons]
  );

  const intakeAnswers = useMemo(
    () => toTextList(caseDetail?.intakeAnswers || null),
    [caseDetail?.intakeAnswers]
  );

  const accessibilityNeeds = useMemo(
    () => toTextList(caseDetail?.accessibilityNeeds || null),
    [caseDetail?.accessibilityNeeds]
  );

  const generatedSummary = useMemo(
    () => buildGeneratedIntakeSummary(intakeForm, caseDetail),
    [intakeForm, caseDetail]
  );

  const currentIntakeSnapshot = useMemo(
    () => buildIntakePersistSnapshot(intakeForm),
    [intakeForm]
  );
  const isIntakeDirty = isIntakePersistDirty(
    intakeForm,
    savedIntakeBaseline,
    intakeHydrated
  );
  isIntakeDirtyRef.current = isIntakeDirty;

  useEffect(() => {
    if (!caseDetail) {
      setPendingDraftAwaitingCompletion(false);
      return;
    }

    setPendingDraftAwaitingCompletion(
      hasSavedDraftAwaitingCompletion(
        caseDetail.intakeAnswers,
        caseDetail.intakeDraft
      )
    );
  }, [caseDetail]);

  useEffect(() => {
    if (
      hasRecordedConsent &&
      isConsentRequiredForwardError(forwardError)
    ) {
      setForwardError(null);
    }
  }, [hasRecordedConsent, forwardError]);

  const showPendingDraftReminder =
    pendingDraftAwaitingCompletion && !isIntakeDirty;

  const intakeProgressStatus: IntakeProgressStatus =
    isSavingProgress &&
    progressInFlightSnapshot !== null &&
    progressInFlightSnapshot === currentIntakeSnapshot
      ? "saving"
      : progressSaveError
        ? "failed"
        : !intakeHydrated || currentIntakeSnapshot === savedIntakeBaseline
          ? "saved"
          : "unsaved";
  const intakeProgressStatusLabel =
    intakeProgressStatus === "saving"
      ? "Saving…"
      : intakeProgressStatus === "failed"
        ? "Save failed"
        : intakeProgressStatus === "unsaved"
          ? "Unsaved changes"
          : "Saved";
  const canSaveProgress =
    Boolean(workflow?.canEditIntake) &&
    !isSavingProgress &&
    !isSavingIntake &&
    isIntakeDirty;
  const formDisabled = !workflow?.canEditIntake || isSavingIntake;

  const stayOnIntakeLeaveDialog = useCallback(() => {
    pendingIntakeLeaveActionRef.current = null;
    setIsIntakeLeaveDialogOpen(false);
  }, []);

  const requestIntakeLeave = useCallback((proceed: () => void) => {
    if (
      !shouldGuardIntakeLeave(
        activeAreaRef.current,
        isIntakeDirtyRef.current
      )
    ) {
      proceed();
      return;
    }

    pendingIntakeLeaveActionRef.current = proceed;
    setIsIntakeLeaveDialogOpen(true);
  }, []);

  const confirmLeaveIntakeWithoutSaving = useCallback(() => {
    const proceed = pendingIntakeLeaveActionRef.current;
    pendingIntakeLeaveActionRef.current = null;

    const restored = cloneIntakeFormState(savedIntakeFormRef.current);
    intakeFormRef.current = restored;
    isIntakeDirtyRef.current = false;
    setIntakeForm(restored);
    setIsIntakeLeaveDialogOpen(false);

    if (proceed) {
      // Run after close so the dialog unregisters before destination changes.
      window.setTimeout(() => {
        proceed();
      }, 0);
    }
  }, []);

  useEffect(() => {
    if (!registerIntakeLeaveGuard) return;

    registerIntakeLeaveGuard({
      isDirty: () =>
        shouldGuardIntakeLeave(
          activeAreaRef.current,
          isIntakeDirtyRef.current
        ),
      requestLeave: requestIntakeLeave,
    });

    return () => registerIntakeLeaveGuard(null);
  }, [registerIntakeLeaveGuard, requestIntakeLeave]);

  useEffect(() => {
    if (!shouldGuardIntakeLeave(activeArea, isIntakeDirty)) return;

    function handleBeforeUnload(event: BeforeUnloadEvent) {
      event.preventDefault();
    }

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [activeArea, isIntakeDirty]);
  const isAnyForwarding = isForwardingMatch || isForwardingManualProvider;

  const assignmentLabel = getCaseAssignmentLabel(
    caseDetail?.assignedNavigatorUserId,
    caseDetail?.assignedNavigatorName,
    workflow?.isAssignedToMe
  );
  const canAssignCaseToMe = canShowAssignToMeAndStart(
    workflow?.isClosed,
    workflow?.isAssignedToMe,
    caseDetail?.assignedNavigatorUserId
  );
  const commandNotice = canAssignCaseToMe
    ? "This case is unassigned. Assign it to yourself to start working."
    : getWorkspaceNotice(workflow);

  function updateIntakeForm<K extends keyof IntakeFormState>(
    key: K,
    value: IntakeFormState[K]
  ) {
    setIntakeForm((current) => ({
      ...current,
      [key]: value,
    }));
  }

  function updatePatientDetailsForm<K extends keyof PatientDetailsFormState>(
    key: K,
    value: PatientDetailsFormState[K]
  ) {
    setPatientDetailsForm((current) => ({
      ...current,
      [key]: value,
    }));
  }

  async function loadPatientCityOptions() {
    setIsLoadingPatientCities(true);
    setPatientCitiesError(null);

    try {
      const response = await fetch("/api/cities", { credentials: "include" });
      if (!response.ok) {
        throw new Error("Unable to load cities.");
      }

      const payload = await response.json();
      setPatientCityOptions(normalizeCityOptions(payload));
    } catch (err) {
      setPatientCityOptions([]);
      setPatientCitiesError(
        err instanceof Error ? err.message : "Unable to load cities."
      );
    } finally {
      setIsLoadingPatientCities(false);
    }
  }

  function openPatientDetailsModal() {
    if (!caseDetail) return;

    setPatientDetailsForm(getPatientDetailsInitialForm(caseDetail));
    setPatientDetailsSaveError(null);
    setPatientDetailsSaveSuccess(null);
    setIsPatientDetailsModalOpen(true);
    void loadPatientCityOptions();
  }

  function closePatientDetailsModal() {
    if (isSavingPatientDetails) return;
    setIsPatientDetailsModalOpen(false);
    setPatientDetailsSaveError(null);
  }

  function resetManualProviderSelection() {
    setManualProviderSearchQuery("");
    setManualProviderSearchResults([]);
    setManualProviderSearchError(null);
    setSelectedManualProvider(null);
    setManualSelectionReason("");
    setManualSelectionNote("");
  }

  function openManualProviderModal() {
    if (!patientDetailsComplete) {
      setForwardError(
        `Complete patient details before forwarding: ${formatMissingPatientDetails(
          patientDetailsMissing
        )}.`
      );

      if (canEditPatientDetails) {
        openPatientDetailsModal();
      }

      return;
    }

    if (!workflow?.canForwardToProvider) {
      setForwardError("This case is not ready to forward to a provider yet.");
      openArea("matching");
      return;
    }

    setForwardError(null);
    setForwardSuccess(null);
    setManualProviderSearchError(null);
    setSelectedManualProvider(null);
    setManualSelectionReason("");
    setManualSelectionNote("");
    setIsManualProviderModalOpen(true);
  }

  function closeManualProviderModal() {
    if (isForwardingManualProvider) return;
    setIsManualProviderModalOpen(false);
    resetManualProviderSelection();
  }

  function handleCompleteIntakeCommand() {
    if (!patientDetailsComplete) {
      setIntakeSaveError(
        `Complete patient details before intake: ${formatMissingPatientDetails(
          patientDetailsMissing
        )}.`
      );

      if (canEditPatientDetails) {
        openPatientDetailsModal();
      }

      return;
    }

    openArea("intake");
  }

  function handleFindMatchesCommand() {
    if (!patientDetailsComplete) {
      setMatchRunError(
        `Complete patient details before matching: ${formatMissingPatientDetails(
          patientDetailsMissing
        )}.`
      );

      if (canEditPatientDetails) {
        openPatientDetailsModal();
      }

      return;
    }

    openArea("matching");
    handleRunMatch();
  }

  function handleForwardToProviderCommand() {
    if (!patientDetailsComplete) {
      setForwardError(
        `Complete patient details before forwarding: ${formatMissingPatientDetails(
          patientDetailsMissing
        )}.`
      );

      if (canEditPatientDetails) {
        openPatientDetailsModal();
      }

      return;
    }

    openArea("matching");
  }

  async function handleAssignToMeAndStart() {
    if (
      isAssigningCaseRef.current ||
      isAssigningCase ||
      !caseIdNumber ||
      !canAssignCaseToMe
    ) {
      return;
    }

    isAssigningCaseRef.current = true;
    setIsAssigningCase(true);
    setAssignCaseError(null);

    try {
      const response = await portalFetch(
        `/api/portal/community-navigator/cases/${caseIdNumber}/assign`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      let payload: { ok?: boolean; alreadyAssigned?: boolean; error?: string } | null =
        null;

      try {
        payload = (await response.json()) as {
          ok?: boolean;
          alreadyAssigned?: boolean;
          error?: string;
        };
      } catch {
        payload = null;
      }

      if (!response.ok || payload?.ok !== true) {
        setAssignCaseError(payload?.error || "Unable to assign this case.");

        if (response.status === 409) {
          await loadCaseDetail();
        }

        return;
      }

      // alreadyAssigned is an idempotent success for the current Navigator.
      setAssignCaseError(null);
      await loadCaseDetail();
    } catch (err) {
      setAssignCaseError(
        err instanceof Error ? err.message : "Unable to assign this case."
      );
    } finally {
      isAssigningCaseRef.current = false;
      setIsAssigningCase(false);
    }
  }

  function openCloseCaseModal() {
    if (!workflow?.canCloseCase) return;

    setCloseCaseReason("");
    setCloseCaseNote("");
    setCloseCaseError(null);
    setIsCloseCaseModalOpen(true);
  }

  function closeCloseCaseModal() {
    if (isClosingCase) return;

    setIsCloseCaseModalOpen(false);
    setCloseCaseReason("");
    setCloseCaseNote("");
    setCloseCaseError(null);
  }

  async function handleCloseCaseSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!caseIdNumber || isClosingCase) return;

    const reason = closeCaseReason.trim();
    const note = closeCaseNote.trim();

    if (!reason) {
      setCloseCaseError("Select a closure reason.");
      return;
    }

    if (reason === "Administrative closure" && !note) {
      setCloseCaseError(
        "An internal note is required for Administrative closure."
      );
      return;
    }

    setIsClosingCase(true);
    setCloseCaseError(null);

    try {
      const response = await portalFetch(
        `/api/portal/community-navigator/cases/${caseIdNumber}/close`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            closedReason: reason,
            note: note || null,
          }),
        }
      );

      const payload = (await response.json()) as CloseCaseResponse;

      if (!response.ok || !payload.ok) {
        throw new Error(payload?.error || "Unable to close this case.");
      }

      suppressCloseReturnFocusRef.current = true;
      pendingCloseHeadingFocusRef.current = true;
      setIsCloseCaseModalOpen(false);
      setCloseCaseReason("");
      setCloseCaseNote("");
      setCloseCaseError(null);
      await loadCaseDetail();
    } catch (err) {
      setCloseCaseError(
        err instanceof Error ? err.message : "Unable to close this case."
      );
    } finally {
      setIsClosingCase(false);
    }
  }

  async function handleSavePatientDetails(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!caseDetail || !caseIdNumber) return;

    if (!patientDetailsForm.postalCode.trim()) {
      setPatientDetailsSaveError("Full postal code is required.");
      return;
    }

    if (!patientDetailsForm.gender.trim()) {
      setPatientDetailsSaveError("Gender is required.");
      return;
    }

    if (
      !patientDetailsForm.email.trim() &&
      !patientDetailsForm.phoneNumber.trim() &&
      !patientDetailsForm.applicantEmail.trim() &&
      !patientDetailsForm.applicantPhone.trim() &&
      !patientDetailsForm.navigatorContactPhone.trim()
    ) {
      setPatientDetailsSaveError(
        "At least one contact method is required: email or phone."
      );
      return;
    }

    setIsSavingPatientDetails(true);
    setPatientDetailsSaveError(null);
    setPatientDetailsSaveSuccess(null);
    setIntakeSaveError(null);
    setMatchRunError(null);
    setForwardError(null);

    try {
      const response = await portalFetch(
        `/api/portal/community-navigator/cases/${caseIdNumber}`,
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            postalCode: patientDetailsForm.postalCode.trim(),
            gender: patientDetailsForm.gender,
            patientCityId: patientDetailsForm.patientCityId
              ? Number.parseInt(patientDetailsForm.patientCityId, 10)
              : null,
            email: patientDetailsForm.email.trim() || null,
            phoneNumber: patientDetailsForm.phoneNumber.trim() || null,
            applicantEmail: patientDetailsForm.applicantEmail.trim() || null,
            applicantPhone: patientDetailsForm.applicantPhone.trim() || null,
            navigatorContactPhone:
              patientDetailsForm.navigatorContactPhone.trim() || null,
          }),
        }
      );

      const payload = (await response.json()) as PatientDetailsPatchResponse;

      if (!response.ok || !payload.ok) {
        throw new Error(payload?.error || "Unable to update patient details.");
      }

      setData(payload);
      setPatientDetailsSaveSuccess("Patient details updated.");
      suppressPatientReturnFocusRef.current = true;
      pendingPatientSuccessFocusRef.current = true;
      setIsPatientDetailsModalOpen(false);
    } catch (err) {
      setPatientDetailsSaveError(
        err instanceof Error ? err.message : "Unable to update patient details."
      );
    } finally {
      setIsSavingPatientDetails(false);
    }
  }

  async function completeNavigatorIntake() {
    if (!caseDetail || !caseIdNumber) return;

    if (!patientDetailsComplete) {
      setIntakeSaveError(
        `Complete patient details before saving intake: ${formatMissingPatientDetails(
          patientDetailsMissing
        )}.`
      );

      if (canEditPatientDetails) {
        openPatientDetailsModal();
      }

      return;
    }

    if (!isNavigatorConsentAccepted(intakeForm.consentAccepted)) {
      setIsConsentRequiredDialogOpen(true);
      return;
    }

    setIsSavingIntake(true);
    setIntakeSaveError(null);
    setIntakeSaveSuccess(null);

    try {
      if (
        intakeForm.priorityOverrideLevel &&
        !intakeForm.priorityOverrideReason
      ) {
        throw new Error("Priority override requires a documented reason.");
      }

      if (
        intakeForm.priorityOverrideReason === "Other" &&
        !intakeForm.priorityOverrideNote.trim()
      ) {
        throw new Error("Other priority override requires a note.");
      }

      const response = await portalFetch(
        `/api/portal/community-navigator/cases/${caseIdNumber}/intake`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            patientCityId: caseDetail.patientCityId,
            preferredLanguageId: getPreferredLanguageIdForSave(
              intakeForm,
              caseDetail
            ),
            gender: caseDetail.gender || caseDetail.patient.gender || null,
            intakeAnswers: buildIntakeAnswers(intakeForm),
            expectedRevision: intakeRevision,
            note: intakeForm.note.trim() || null,
          }),
        }
      );

      const payload = (await response.json()) as IntakeSaveResponse;

      if (!response.ok || !payload.ok) {
        if (
          response.status === 409 &&
          payload?.error === INTAKE_REVISION_CONFLICT_MESSAGE
        ) {
          throw new Error(INTAKE_REVISION_CONFLICT_MESSAGE);
        }

        throw new Error(payload?.error || "Unable to save navigator intake.");
      }

      setIntakeSaveSuccess("Navigator intake saved.");
      const completedSnapshot = buildIntakePersistSnapshot(intakeForm);
      savedIntakeBaselineRef.current = completedSnapshot;
      savedIntakeFormRef.current = cloneIntakeFormState(intakeForm);
      setSavedIntakeBaseline(completedSnapshot);
      setPendingDraftAwaitingCompletion(false);
      setIsIntakeChangesSavedDialogOpen(false);
      allowIntakeHydrateRef.current = true;
      const loaded = await loadCaseDetail();
      if (!loaded) {
        allowIntakeHydrateRef.current = false;
      }
    } catch (err) {
      setIntakeSaveError(
        err instanceof Error ? err.message : "Unable to save navigator intake."
      );
    } finally {
      setIsSavingIntake(false);
    }
  }

  async function handleSaveIntake(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await completeNavigatorIntake();
  }

  async function handleSaveProgress() {
    if (!caseDetail || !caseIdNumber || !workflow?.canEditIntake) return;
    if (isSavingProgress || isSavingIntake) return;

    const answers = buildIntakeAnswers(intakeForm);
    const snapshot = buildIntakePersistSnapshot(intakeForm);
    if (snapshot === savedIntakeBaselineRef.current) return;

    const previouslyCompleted = hasCompletedNavigatorIntake(
      caseDetail.intakeAnswers
    );

    setIsSavingProgress(true);
    setProgressInFlightSnapshot(snapshot);
    setProgressSaveError(null);

    try {
      const response = await portalFetch(
        `/api/portal/community-navigator/cases/${caseIdNumber}/intake/progress`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            expectedRevision: intakeRevision,
            intakeAnswers: answers,
            note: intakeForm.note.trim() || null,
          }),
        }
      );

      const payload = (await response.json()) as {
        ok?: boolean;
        revision?: number;
        error?: string;
      };

      if (!response.ok || payload?.ok !== true) {
        if (
          response.status === 409 &&
          payload?.error === INTAKE_REVISION_CONFLICT_MESSAGE
        ) {
          throw new Error(INTAKE_REVISION_CONFLICT_MESSAGE);
        }

        throw new Error(payload?.error || "Unable to save intake progress.");
      }

      savedIntakeBaselineRef.current = snapshot;
      savedIntakeFormRef.current = cloneIntakeFormState(intakeForm);
      setSavedIntakeBaseline(snapshot);
      const nextRevision = payload.revision;
      if (typeof nextRevision === "number" && Number.isInteger(nextRevision)) {
        setIntakeRevision(nextRevision);
      }

      if (previouslyCompleted) {
        setPendingDraftAwaitingCompletion(true);
        setIsIntakeChangesSavedDialogOpen(true);
      }
    } catch (err) {
      setProgressSaveError(
        err instanceof Error ? err.message : "Unable to save intake progress."
      );
    } finally {
      setIsSavingProgress(false);
      setProgressInFlightSnapshot(null);
    }
  }

  async function handleRunMatch() {
    if (!caseIdNumber || isRunningMatch || !workflow?.canRunMatching) {
      if (!patientDetailsComplete) {
        setMatchRunError(
          `Complete patient details before matching: ${formatMissingPatientDetails(
            patientDetailsMissing
          )}.`
        );

        if (canEditPatientDetails) {
          openPatientDetailsModal();
        }
      }

      return;
    }

    setIsRunningMatch(true);
    setMatchRunError(null);
    setMatchRunSuccess(null);
    setForwardError(null);
    setForwardSuccess(null);

    try {
      const response = await portalFetch(
        `/api/portal/community-navigator/cases/${caseIdNumber}/match-run`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      const payload = (await response.json()) as MatchRunResponse;

      if (!response.ok || !payload.ok) {
        throw new Error(payload?.error || "Unable to run provider matching.");
      }

      const matchedCount = countMatchedProviders(payload.latestMatchResults);
      setMatchRunSuccess(formatMatchRunCompletionMessage(matchedCount));

      openArea("matching");
      await loadCaseDetail();
    } catch (err) {
      setMatchRunError(
        err instanceof Error ? err.message : "Unable to run provider matching."
      );
      openArea("matching");
    } finally {
      setIsRunningMatch(false);
    }
  }

  function handleViewProviderProfile(familyDoctorId: number) {
    const id = Number(familyDoctorId);
    if (!Number.isFinite(id) || id < 1) return;
    setSelectedProviderProfileId(id);
  }

  function handleCloseProviderProfile() {
    setSelectedProviderProfileId(null);
  }

  async function handleMarkPatientInformed(task: NavigatorPatientContactTask) {
    if (!caseIdNumber || recordingPatientContactRequestId !== null) return;

    const providerRequestId = Number(task.providerRequestId);
    if (!Number.isFinite(providerRequestId) || providerRequestId < 1) return;

    setRecordingPatientContactRequestId(providerRequestId);
    setPatientContactError(null);

    try {
      await markCommunityNavigatorPatientContact(
        caseIdNumber,
        task.contactType,
        providerRequestId
      );
      await loadCaseDetail();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Unable to record patient contact.";
      setPatientContactError(message);
    } finally {
      setRecordingPatientContactRequestId(null);
    }
  }

  async function handleForwardMatch(result: NavigatorCaseMatchResult) {
    const providerForwardingState = result as NavigatorCaseMatchResult & {
      alreadyForwarded?: boolean;
      canForwardThisProvider?: boolean;
      providerResponseLabel?: string | null;
    };

    if (
      providerForwardingState.alreadyForwarded ||
      providerForwardingState.canForwardThisProvider === false
    ) {
      setForwardError(
        providerForwardingState.providerResponseLabel
          ? `This case was already forwarded to this provider. Latest response: ${providerForwardingState.providerResponseLabel}.`
          : "This case was already forwarded to this provider."
      );
      openArea("matching");
      return;
    }

    if (!patientDetailsComplete) {
      setForwardError(
        `Complete patient details before forwarding: ${formatMissingPatientDetails(
          patientDetailsMissing
        )}.`
      );

      if (canEditPatientDetails) {
        openPatientDetailsModal();
      }

      return;
    }

    if (!hasRecordedConsent) {
      openArea("matching");
      return;
    }

    if (
      !caseIdNumber ||
      !workflow?.canForwardToProvider ||
      isAnyForwarding
    ) {
      return;
    }

    setIsForwardingMatch(true);
    setForwardingMatchResultId(result.navigatorCaseMatchResultId);
    setForwardError(null);
    setForwardSuccess(null);

    try {
      const response = await portalFetch(
        `/api/portal/community-navigator/cases/${caseIdNumber}/forward`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            navigatorCaseMatchResultId: result.navigatorCaseMatchResultId,
          }),
        }
      );

      const payload = (await response.json()) as ForwardCaseResponse;

      if (!response.ok || !payload.ok) {
        const message =
          payload?.error || "Unable to forward this match to the provider.";

        if (response.status === 409) {
          setForwardError(message);
          openArea("matching");
          await loadCaseDetail();
          return;
        }

        throw new Error(message);
      }

      const requestIdText = payload.registrationRequestId
        ? ` Request #${payload.registrationRequestId} was created.`
        : "";

      const providerName =
        payload.selectedMatch?.familyDoctorName ||
        result.familyDoctorName ||
        "selected provider";

      setForwardSuccess(
        `Navigator request forwarded to ${providerName}.${requestIdText}`
      );

      openArea("follow-up");
      await loadCaseDetail();
    } catch (err) {
      setForwardError(
        err instanceof Error
          ? err.message
          : "Unable to forward this match to the provider."
      );
      openArea("matching");
    } finally {
      setIsForwardingMatch(false);
      setForwardingMatchResultId(null);
    }
  }

  async function handleManualProviderSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const query = manualProviderSearchQuery.trim();

    if (query.length < 2) {
      setManualProviderSearchError(
        "Enter at least 2 characters to search by doctor, clinic, city, CPSO, or CHC intake profile."
      );
      return;
    }

    setIsSearchingManualProviders(true);
    setManualProviderSearchError(null);
    setManualProviderSearchResults([]);
    setSelectedManualProvider(null);

    try {
      const searchParams = new URLSearchParams({
        q: query,
      });

      if (caseIdNumber) {
        searchParams.set("navigatorCaseId", String(caseIdNumber));
      }

      const response = await portalFetch(
        `/api/portal/community-navigator/provider-search?${searchParams.toString()}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      const payload = (await response.json()) as ProviderSearchResponse;

      if (!response.ok || !payload.ok) {
        throw new Error(payload?.error || "Unable to search providers.");
      }

      setManualProviderSearchResults(payload.providers || []);

      if (!payload.providers?.length) {
        setManualProviderSearchError("No providers found for this search.");
      }
    } catch (err) {
      setManualProviderSearchError(
        err instanceof Error ? err.message : "Unable to search providers."
      );
    } finally {
      setIsSearchingManualProviders(false);
    }
  }

  function handleSelectManualProvider(provider: ManualProviderSearchResult) {
    setSelectedManualProvider(provider);
    setForwardError(null);
    setForwardSuccess(null);
  }

  async function handleForwardManualProvider(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!patientDetailsComplete) {
      setForwardError(
        `Complete patient details before forwarding: ${formatMissingPatientDetails(
          patientDetailsMissing
        )}.`
      );

      if (canEditPatientDetails) {
        openPatientDetailsModal();
      }

      return;
    }

    if (!caseIdNumber || !workflow?.canForwardToProvider || isAnyForwarding) {
      return;
    }

    if (!selectedManualProvider) {
      setManualProviderSearchError("Select a provider before forwarding.");
      return;
    }

    if (!selectedManualProvider.isForwardable) {
      setManualProviderSearchError(
        "This provider cannot be forwarded to because of current safety warnings."
      );
      return;
    }

    if (!manualSelectionReason.trim()) {
      setManualProviderSearchError(
        "Select a reason for manually choosing this provider."
      );
      return;
    }

    setIsForwardingManualProvider(true);
    setManualProviderSearchError(null);
    setForwardError(null);
    setForwardSuccess(null);

    try {
      const response = await portalFetch(
        `/api/portal/community-navigator/cases/${caseIdNumber}/forward`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            familyDoctorId: selectedManualProvider.familyDoctorId,
            manualSelectionReason: manualSelectionReason.trim(),
            note: manualSelectionNote.trim() || null,
          }),
        }
      );

      const payload = (await response.json()) as ForwardCaseResponse;

      if (!response.ok || !payload.ok) {
        const message =
          payload?.error ||
          "Unable to forward this case to the selected provider.";

        if (response.status === 409) {
          setForwardError(message);
          setManualProviderSearchError(message);
          openArea("matching");
          await loadCaseDetail();
          return;
        }

        throw new Error(message);
      }

      const requestIdText = payload.registrationRequestId
        ? ` Request #${payload.registrationRequestId} was created.`
        : "";

      const providerName =
        payload.selectedMatch?.familyDoctorName ||
        selectedManualProvider.familyDoctorName ||
        "selected provider";

      setForwardSuccess(
        `Navigator request manually forwarded to ${providerName}.${requestIdText}`
      );
      suppressManualReturnFocusRef.current = true;
      pendingForwardSuccessFocusRef.current = true;

      setIsManualProviderModalOpen(false);
      resetManualProviderSelection();

      openArea("follow-up");
      await loadCaseDetail();
    } catch (err) {
      setManualProviderSearchError(
        err instanceof Error
          ? err.message
          : "Unable to forward this case to the selected provider."
      );
    } finally {
      setIsForwardingManualProvider(false);
    }
  }

  useNavigatorDialog({
    open: isPatientDetailsModalOpen,
    onDismiss: closePatientDetailsModal,
    dismissDisabled: isSavingPatientDetails,
    dialogRef: patientDialogRef,
    initialFocusRef: patientPostalRef,
    suppressReturnFocusRef: suppressPatientReturnFocusRef,
  });

  useNavigatorDialog({
    open: isManualProviderModalOpen,
    onDismiss: closeManualProviderModal,
    dismissDisabled: isForwardingManualProvider,
    dialogRef: manualDialogRef,
    initialFocusRef: manualSearchRef,
    suppressReturnFocusRef: suppressManualReturnFocusRef,
  });

  useNavigatorDialog({
    open: isCloseCaseModalOpen,
    onDismiss: closeCloseCaseModal,
    dismissDisabled: isClosingCase,
    dialogRef: closeDialogRef,
    initialFocusRef: closeReasonRef,
    suppressReturnFocusRef: suppressCloseReturnFocusRef,
  });

  useNavigatorDialog({
    open: isIntakeLeaveDialogOpen,
    onDismiss: stayOnIntakeLeaveDialog,
    dialogRef: intakeLeaveDialogRef,
    initialFocusRef: intakeLeaveStayRef,
  });

  const stayOnConsentRequiredDialog = useCallback(() => {
    setIsConsentRequiredDialogOpen(false);
  }, []);

  const goToBasicsFromConsentRequiredDialog = useCallback(() => {
    setIsConsentRequiredDialogOpen(false);
    goToIntakeBasicsConsent();
  }, [goToIntakeBasicsConsent]);

  const continueEditingAfterIntakeChangesSaved = useCallback(() => {
    setIsIntakeChangesSavedDialogOpen(false);
  }, []);

  function completeIntakeFromChangesSavedDialog() {
    setIsIntakeChangesSavedDialogOpen(false);
    void completeNavigatorIntake();
  }

  function completeIntakeFromPendingDraftReminder() {
    void completeNavigatorIntake();
  }

  useNavigatorDialog({
    open: isConsentRequiredDialogOpen,
    onDismiss: stayOnConsentRequiredDialog,
    dialogRef: consentRequiredDialogRef,
    initialFocusRef: consentRequiredGoToBasicsRef,
  });

  useNavigatorDialog({
    open: isIntakeChangesSavedDialogOpen,
    onDismiss: continueEditingAfterIntakeChangesSaved,
    dismissDisabled: isSavingIntake,
    dialogRef: intakeChangesSavedDialogRef,
    initialFocusRef: intakeChangesSavedCompleteRef,
  });

  useLayoutEffect(() => {
    if (!isPatientDetailsModalOpen || !patientDetailsSaveError) return;

    if (patientDetailsSaveError.startsWith("Full postal code")) {
      patientPostalRef.current?.focus();
      return;
    }

    if (patientDetailsSaveError.startsWith("Gender")) {
      patientGenderRef.current?.focus();
      return;
    }

    patientErrorRef.current?.focus();
  }, [isPatientDetailsModalOpen, patientDetailsSaveError]);

  useLayoutEffect(() => {
    if (!pendingPatientSuccessFocusRef.current || isPatientDetailsModalOpen) {
      return;
    }

    const node = patientSuccessRef.current;
    if (!node) return;
    pendingPatientSuccessFocusRef.current = false;
    node.focus();
  }, [patientDetailsSaveSuccess, isPatientDetailsModalOpen]);

  useLayoutEffect(() => {
    if (!isManualProviderModalOpen || !manualProviderSearchError) return;
    manualErrorRef.current?.focus();
  }, [isManualProviderModalOpen, manualProviderSearchError]);

  useLayoutEffect(() => {
    if (!pendingForwardSuccessFocusRef.current) return;
    if (isManualProviderModalOpen || isLoading) return;

    if (loadError) {
      pendingForwardSuccessFocusRef.current = false;
      return;
    }

    if (activeArea !== "follow-up") return;

    const node = forwardSuccessRef.current;
    if (!node) return;
    pendingForwardSuccessFocusRef.current = false;
    node.focus();
  }, [
    activeArea,
    forwardSuccess,
    isLoading,
    isManualProviderModalOpen,
    loadError,
  ]);

  useLayoutEffect(() => {
    if (!isCloseCaseModalOpen || !closeCaseError) return;

    if (closeCaseError === "Select a closure reason.") {
      closeReasonRef.current?.focus();
      return;
    }

    if (closeCaseError.startsWith("An internal note")) {
      closeNoteRef.current?.focus();
      return;
    }

    closeErrorRef.current?.focus();
  }, [closeCaseError, isCloseCaseModalOpen]);

  useLayoutEffect(() => {
    if (
      !pendingCloseHeadingFocusRef.current ||
      isCloseCaseModalOpen ||
      isLoading
    ) {
      return;
    }

    if (loadError) {
      pendingCloseHeadingFocusRef.current = false;
      return;
    }

    const node = caseHeadingRef.current;
    if (!node) return;
    pendingCloseHeadingFocusRef.current = false;
    node.focus();
  }, [isCloseCaseModalOpen, isLoading, loadError]);

  return (
    <div className="cn-case-detail-page">
      <div className="cn-case-detail-back-row">
        <Link
          to={casesBackNavigation.to}
          state={casesBackNavigation.state}
          onClick={(event) => {
            if (!shouldGuardIntakeLeave(activeArea, isIntakeDirty)) return;
            event.preventDefault();
            requestIntakeLeave(() => {
              navigate(casesBackNavigation.to, {
                state: casesBackNavigation.state,
              });
            });
          }}
        >
          ← Back to cases
        </Link>
      </div>

      {isLoading ? (
        <section className="cn-case-detail-state-card">
          Loading navigator case...
        </section>
      ) : null}

      {loadError ? (
        <section className="cn-case-detail-alert" role="alert">
          <h1>Case could not load</h1>
          <p>{loadError}</p>
        </section>
      ) : null}

      {!isLoading && !loadError && caseDetail ? (
        <>
          <section className="cn-case-detail-context" aria-label="Case context">
            <div>
              <h1 ref={caseHeadingRef} tabIndex={-1}>
                {formatPatientName(caseDetail)}
              </h1>
              <p>Case #{caseDetail.navigatorCaseId}</p>
            </div>
            <dl>
              <div>
                <dt>Status</dt>
                <dd>{formatStatus(caseDetail.status)}</dd>
              </div>
              <div>
                <dt>Priority</dt>
                <dd>{caseDetail.priorityLevel || "—"}</dd>
              </div>
              <div>
                <dt>Assigned to</dt>
                <dd>{assignmentLabel}</dd>
              </div>
            </dl>
          </section>

          <WorkspaceCommandCard
            workflow={workflow}
            nextAction={data?.nextAction || null}
            isRunningMatch={isRunningMatch}
            isClosingCase={isClosingCase}
            notice={commandNotice}
            canAssignToMe={canAssignCaseToMe}
            isAssigningCase={isAssigningCase}
            assignError={assignCaseError}
            onAssignToMe={handleAssignToMeAndStart}
            onCompleteIntake={handleCompleteIntakeCommand}
            onFindMatches={handleFindMatchesCommand}
            onForwardToProvider={handleForwardToProviderCommand}
            onCloseCase={openCloseCaseModal}
          />

          {patientDetailsSaveSuccess ? (
            <section
              className="cn-case-detail-success"
              role="status"
              tabIndex={-1}
              ref={patientSuccessRef}
            >
              <p>{patientDetailsSaveSuccess}</p>
            </section>
          ) : null}

          <div
            className="cn-case-detail-tabs"
            role="tablist"
            aria-label="Case work areas"
          >
            {caseDetailAreaTabs.map((tab) => (
              <button
                key={tab.area}
                type="button"
                role="tab"
                id={`cn-case-tab-${tab.area}`}
                aria-selected={activeArea === tab.area}
                aria-controls={`cn-case-panel-${tab.area}`}
                className={
                  activeArea === tab.area
                    ? "cn-case-detail-tab active"
                    : "cn-case-detail-tab"
                }
                onClick={() => {
                  if (tab.area === activeArea) return;
                  requestIntakeLeave(() => openArea(tab.area));
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div
            className="cn-case-detail-panel"
            role="tabpanel"
            id={`cn-case-panel-${activeArea}`}
            aria-labelledby={`cn-case-tab-${activeArea}`}
          >
            {activeArea === "patient" ? (
              <>
                {!patientDetailsComplete ? (
                  <section className="cn-case-detail-patient-warning">
                    <div>
                      <p className="cn-case-detail-eyebrow">
                        Patient details incomplete
                      </p>
                      <h2>Complete patient details before intake completion</h2>
                      <p>
                        Confirm{" "}
                        {formatMissingPatientDetails(patientDetailsMissing)}{" "}
                        before saving intake, running matching, or forwarding to
                        a provider. You can also update contact details here.
                        Patient name and date of birth are locked.
                      </p>

                      <ul>
                        {patientDetailsMissing.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>

                      {!canEditPatientDetails ? (
                        <p className="cn-case-detail-warning-note">
                          Assign this case to yourself before updating patient
                          details.
                        </p>
                      ) : null}
                    </div>

                    <button
                      type="button"
                      className="cn-case-detail-primary-button"
                      onClick={openPatientDetailsModal}
                      disabled={!canEditPatientDetails}
                    >
                      Update patient details
                    </button>
                  </section>
                ) : null}

                <PatientDetailsCard
                  caseDetail={caseDetail}
                  assignmentLabel={assignmentLabel}
                  canEditPatientDetails={canEditPatientDetails}
                  onUpdatePatientDetails={openPatientDetailsModal}
                />
              </>
            ) : null}

            {activeArea === "intake" ? (
              <NavigatorIntakeForm
                workflow={workflow}
                intakeForm={intakeForm}
                generatedSummary={generatedSummary}
                formDisabled={formDisabled}
                isSavingIntake={isSavingIntake}
                intakeSaveError={intakeSaveError}
                intakeSaveSuccess={intakeSaveSuccess}
                progressStatus={intakeProgressStatus}
                progressStatusLabel={intakeProgressStatusLabel}
                progressSaveError={progressSaveError}
                canSaveProgress={canSaveProgress}
                isSavingProgress={isSavingProgress}
                priorityReasons={priorityReasons}
                intakeAnswers={intakeAnswers}
                accessibilityNeeds={accessibilityNeeds}
                stepNavigationRequest={intakeStepNavigationRequest}
                showPendingDraftReminder={showPendingDraftReminder}
                onSubmit={handleSaveIntake}
                onSaveProgress={handleSaveProgress}
                onCompleteIntakeFromReminder={
                  completeIntakeFromPendingDraftReminder
                }
                onChange={updateIntakeForm}
              />
            ) : null}

            {activeArea === "matching" ? (
              <MatchResultsSection
                latestMatchRun={data?.latestMatchRun}
                matchResults={matchResults}
                workflow={workflow}
                nextAction={data?.nextAction || null}
                matchRunError={matchRunError}
                matchRunSuccess={matchRunSuccess}
                forwardError={forwardError}
                forwardSuccess={forwardSuccess}
                isForwardingMatch={isAnyForwarding}
                forwardingMatchResultId={forwardingMatchResultId}
                showConsentRequiredWarning={!hasRecordedConsent}
                onUpdateConsent={goToIntakeBasicsConsent}
                onForwardMatch={handleForwardMatch}
                onViewProviderProfile={handleViewProviderProfile}
                onChooseAnotherProvider={openManualProviderModal}
              />
            ) : null}

            {activeArea === "follow-up" ? (
              <div className="cn-case-detail-follow-up">
                {forwardSuccess ? (
                  <div
                    className="cn-case-detail-form-success"
                    role="status"
                    tabIndex={-1}
                    ref={forwardSuccessRef}
                  >
                    {forwardSuccess}
                  </div>
                ) : null}

                <PatientPhoneFollowUpPanel
                  tasks={data?.patientContactTasks || []}
                  phoneNumber={
                    data?.patientContact?.phoneNumber ||
                    caseDetail.patient.phoneNumber
                  }
                  recordingProviderRequestId={recordingPatientContactRequestId}
                  error={patientContactError}
                  onMarkInformed={handleMarkPatientInformed}
                  onViewProviderProfile={handleViewProviderProfile}
                  onFindAnotherProvider={() => openArea("matching")}
                  canFindAnotherProvider={workflow?.canRunMatching === true}
                />

                <ProviderSubmissionCard
                  caseDetail={caseDetail}
                  nextActionCode={data?.nextAction?.code || null}
                  nextActionLabel={data?.nextAction?.label || "—"}
                  canCloseCase={workflow?.canCloseCase === true}
                  canRunMatching={workflow?.canRunMatching === true}
                  showNoEmailPhoneHint={
                    data?.patientContact?.hasDeliverableEmail === false &&
                    data?.nextAction?.code === "FindAnotherMatch"
                  }
                  onOpenMatching={() => openArea("matching")}
                  onOpenPatient={() => openArea("patient")}
                  onCloseCase={openCloseCaseModal}
                  onViewProviderProfile={handleViewProviderProfile}
                />
                <ActiveWaitlistsCard
                  waitlists={caseDetail.activeWaitlists || []}
                  outstandingPatientContactRequestIds={(
                    data?.patientContactTasks || []
                  )
                    .filter((task) => task.contactType === "Waitlist")
                    .map((task) => Number(task.providerRequestId))}
                  onViewProviderProfile={handleViewProviderProfile}
                />
                <ProviderRequestHistory
                  history={caseDetail.providerRequestHistory || []}
                />
                <ActivityTimeline activity={activity} />
                <ClosureDetailsCard caseDetail={caseDetail} />
              </div>
            ) : null}
          </div>

          {selectedProviderProfileId ? (
            <ProviderMatchingProfileModal
              familyDoctorId={selectedProviderProfileId}
              onClose={handleCloseProviderProfile}
            />
          ) : null}

          {isManualProviderModalOpen ? (
            <div
              className="cn-case-detail-modal-backdrop"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  closeManualProviderModal();
                }
              }}
            >
              <section
                className="cn-case-detail-modal cn-manual-provider-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="cn-manual-provider-modal-title"
                tabIndex={-1}
                ref={manualDialogRef}
              >
                <div className="cn-case-detail-modal-header">
                  <div>
                    <p className="cn-case-detail-eyebrow">
                      Manual provider selection
                    </p>
                    <h2 id="cn-manual-provider-modal-title">
                      Choose another provider
                    </h2>
                    <p>
                      Use this when a navigator needs to forward the case to a
                      specific doctor, CHC-style intake profile, or community
                      pathway outside the top recommended matches.
                    </p>
                  </div>

                  <button
                    type="button"
                    className="cn-case-detail-modal-close"
                    onClick={closeManualProviderModal}
                    disabled={isForwardingManualProvider}
                    aria-label="Close manual provider selection dialog"
                  >
                    ×
                  </button>
                </div>

                {manualProviderSearchError ? (
                  <div
                    className="cn-case-detail-modal-error"
                    role="alert"
                    tabIndex={-1}
                    ref={manualErrorRef}
                  >
                    {manualProviderSearchError}
                  </div>
                ) : null}

                <form
                  className="cn-manual-provider-search-form"
                  onSubmit={handleManualProviderSearch}
                >
                  <label>
                    <span>Search provider or clinic</span>
                    <input
                      ref={manualSearchRef}
                      type="search"
                      value={manualProviderSearchQuery}
                      onChange={(event) =>
                        setManualProviderSearchQuery(event.target.value)
                      }
                      placeholder="Search doctor, clinic, city, CPSO, or CHC intake"
                      disabled={
                        isSearchingManualProviders ||
                        isForwardingManualProvider
                      }
                    />
                  </label>

                  <button
                    type="submit"
                    className="cn-case-detail-primary-button"
                    disabled={
                      isSearchingManualProviders ||
                      isForwardingManualProvider
                    }
                  >
                    {isSearchingManualProviders ? "Searching..." : "Search"}
                  </button>
                </form>

                <div className="cn-manual-provider-results">
                  {manualProviderSearchResults.map((provider) => {
                    const isSelected =
                      selectedManualProvider?.familyDoctorId ===
                      provider.familyDoctorId;

                    return (
                      <article
                        className={[
                          "cn-manual-provider-result-card",
                          isSelected ? "is-selected" : "",
                          !provider.isForwardable ? "is-not-forwardable" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        key={provider.familyDoctorId}
                      >
                        <div>
                          <h3>{provider.familyDoctorName}</h3>
                          <p>
                            {provider.clinicName || "Clinic not specified"}
                            {provider.cityName ? ` · ${provider.cityName}` : ""}
                            {provider.clinicFsa
                              ? ` · ${provider.clinicFsa}`
                              : ""}
                          </p>

                          <dl className="cn-manual-provider-mini-grid">
                            <div>
                              <dt>CPSO</dt>
                              <dd>{provider.cpsO || "—"}</dd>
                            </div>
                            <div>
                              <dt>Accepting</dt>
                              <dd>
                                {provider.acceptingNewPatients ? "Yes" : "No"}
                              </dd>
                            </div>
                            <div>
                              <dt>Remaining capacity</dt>
                              <dd>{formatCapacity(provider.remainingCapacity)}</dd>
                            </div>
                            <div>
                              <dt>Active backlog</dt>
                              <dd>{provider.activePipelineCount}</dd>
                            </div>
                          </dl>

                          <p
                            className={
                              provider.warnings.length
                                ? "cn-manual-provider-warning-text"
                                : "cn-manual-provider-safe-text"
                            }
                          >
                            {getProviderSearchWarningText(provider)}
                          </p>
                        </div>

                        <button
                          type="button"
                          className="cn-case-detail-secondary-button"
                          onClick={() => handleSelectManualProvider(provider)}
                          disabled={
                            isForwardingManualProvider ||
                            !provider.isForwardable
                          }
                        >
                          {getManualProviderSelectLabel(provider, isSelected)}
                        </button>
                      </article>
                    );
                  })}
                </div>

                {selectedManualProvider ? (
                  <form
                    className="cn-manual-provider-forward-form"
                    onSubmit={handleForwardManualProvider}
                  >
                    <div className="cn-manual-provider-selected-card">
                      <p className="cn-case-detail-eyebrow">
                        Selected provider
                      </p>
                      <h3>{selectedManualProvider.familyDoctorName}</h3>
                      <p>
                        {selectedManualProvider.clinicName ||
                          "Clinic not specified"}
                        {selectedManualProvider.cityName
                          ? ` · ${selectedManualProvider.cityName}`
                          : ""}
                      </p>
                    </div>

                    <label>
                      <span>Reason for manual selection *</span>
                      <select
                        value={manualSelectionReason}
                        onChange={(event) =>
                          setManualSelectionReason(event.target.value)
                        }
                        disabled={isForwardingManualProvider}
                        required
                      >
                        <option value="">Select reason</option>
                        {manualSelectionReasonOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label>
                      <span>Optional internal note</span>
                      <textarea
                        value={manualSelectionNote}
                        onChange={(event) =>
                          setManualSelectionNote(event.target.value)
                        }
                        placeholder="Example: Discussed with clinic by phone; navigator directed to send this case here."
                        rows={3}
                        disabled={isForwardingManualProvider}
                      />
                    </label>

                    <div className="cn-case-detail-modal-actions">
                      <button
                        type="button"
                        className="cn-case-detail-secondary-button"
                        onClick={closeManualProviderModal}
                        disabled={isForwardingManualProvider}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="cn-case-detail-primary-button"
                        disabled={
                          isForwardingManualProvider ||
                          !selectedManualProvider.isForwardable
                        }
                      >
                        {isForwardingManualProvider
                          ? "Forwarding..."
                          : "Forward case"}
                      </button>
                    </div>
                  </form>
                ) : null}
              </section>
            </div>
          ) : null}

          {isPatientDetailsModalOpen ? (
            <div
              className="cn-case-detail-modal-backdrop"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  closePatientDetailsModal();
                }
              }}
            >
              <section
                className="cn-case-detail-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="cn-patient-details-modal-title"
                tabIndex={-1}
                ref={patientDialogRef}
              >
                <div className="cn-case-detail-modal-header">
                  <div>
                    <p className="cn-case-detail-eyebrow">Patient details</p>
                    <h2 id="cn-patient-details-modal-title">
                      Update patient details
                    </h2>
                    <p>
                      Update safe patient/contact fields. Name and date of birth
                      are locked because they are identity and deduplication
                      fields.
                    </p>
                  </div>

                  <button
                    type="button"
                    className="cn-case-detail-modal-close"
                    onClick={closePatientDetailsModal}
                    disabled={isSavingPatientDetails}
                    aria-label="Close patient details dialog"
                  >
                    ×
                  </button>
                </div>

                {patientDetailsSaveError ? (
                  <div
                    className="cn-case-detail-modal-error"
                    role="alert"
                    tabIndex={-1}
                    ref={patientErrorRef}
                  >
                    {patientDetailsSaveError}
                  </div>
                ) : null}

                <form
                  className="cn-case-detail-modal-form"
                  onSubmit={handleSavePatientDetails}
                >
                  <div className="cn-case-detail-modal-readonly">
                    <span>Patient name</span>
                    <p className="cn-case-detail-modal-readonly-value">
                      {formatPatientName(caseDetail)}
                    </p>
                    <small>
                      Name is locked. Use a future identity correction workflow
                      if needed.
                    </small>
                  </div>

                  <div className="cn-case-detail-modal-readonly">
                    <span>Date of birth</span>
                    <p className="cn-case-detail-modal-readonly-value">
                      {formatDateOnly(caseDetail.patient.dateOfBirth)}
                    </p>
                    <small>
                      Date of birth is locked for patient identity safety.
                    </small>
                  </div>

                  <label>
                    <span>Full postal code *</span>
                    <input
                      ref={patientPostalRef}
                      type="text"
                      value={patientDetailsForm.postalCode}
                      onChange={(event) =>
                        updatePatientDetailsForm(
                          "postalCode",
                          event.target.value
                        )
                      }
                      placeholder="e.g. L6M 5P7"
                      autoComplete="postal-code"
                      disabled={isSavingPatientDetails}
                      required
                    />
                    {caseDetail.patientFsa ? (
                      <small>Current/Uploaded FSA: {caseDetail.patientFsa}</small>
                    ) : null}
                  </label>

                  <label>
                    <span>Gender *</span>
                    <select
                      ref={patientGenderRef}
                      value={patientDetailsForm.gender}
                      onChange={(event) =>
                        updatePatientDetailsForm("gender", event.target.value)
                      }
                      disabled={isSavingPatientDetails}
                      required
                    >
                      <option value="">Select gender</option>
                      {patientGenderOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    <span>City</span>
                    <select
                      value={patientDetailsForm.patientCityId}
                      onChange={(event) =>
                        updatePatientDetailsForm(
                          "patientCityId",
                          event.target.value
                        )
                      }
                      disabled={
                        isSavingPatientDetails ||
                        isLoadingPatientCities ||
                        Boolean(patientCitiesError)
                      }
                    >
                      <option value="">
                        {isLoadingPatientCities
                          ? "Loading cities…"
                          : "Select city"}
                      </option>
                      {patientCityOptions.map((city) => (
                        <option key={city.CityID} value={String(city.CityID)}>
                          {city.Name}
                        </option>
                      ))}
                    </select>
                    {patientCitiesError ? (
                      <small>{patientCitiesError}</small>
                    ) : null}
                  </label>

                  <label>
                    <span>Patient email</span>
                    <input
                      type="email"
                      value={patientDetailsForm.email}
                      onChange={(event) =>
                        updatePatientDetailsForm("email", event.target.value)
                      }
                      placeholder="patient@example.com"
                      autoComplete="email"
                      disabled={isSavingPatientDetails}
                    />
                  </label>

                  <label>
                    <span>Patient phone</span>
                    <input
                      type="tel"
                      value={patientDetailsForm.phoneNumber}
                      onChange={(event) =>
                        updatePatientDetailsForm(
                          "phoneNumber",
                          event.target.value
                        )
                      }
                      placeholder="905-555-1234"
                      autoComplete="tel"
                      disabled={isSavingPatientDetails}
                    />
                  </label>

                  <label>
                    <span>Applicant email</span>
                    <input
                      type="email"
                      value={patientDetailsForm.applicantEmail}
                      onChange={(event) =>
                        updatePatientDetailsForm(
                          "applicantEmail",
                          event.target.value
                        )
                      }
                      placeholder="applicant@example.com"
                      disabled={isSavingPatientDetails}
                    />
                  </label>

                  <label>
                    <span>Applicant phone</span>
                    <input
                      type="tel"
                      value={patientDetailsForm.applicantPhone}
                      onChange={(event) =>
                        updatePatientDetailsForm(
                          "applicantPhone",
                          event.target.value
                        )
                      }
                      placeholder="905-555-1234"
                      disabled={isSavingPatientDetails}
                    />
                  </label>

                  <label className="cn-case-detail-modal-form-full">
                    <span>Navigator contact phone</span>
                    <input
                      type="tel"
                      value={patientDetailsForm.navigatorContactPhone}
                      onChange={(event) =>
                        updatePatientDetailsForm(
                          "navigatorContactPhone",
                          event.target.value
                        )
                      }
                      placeholder="905-555-1234"
                      disabled={isSavingPatientDetails}
                    />
                    <small>
                      Use this when the patient has no email and navigator
                      follow-up is phone-based.
                    </small>
                  </label>

                  <div className="cn-case-detail-modal-actions">
                    <button
                      type="button"
                      className="cn-case-detail-secondary-button"
                      onClick={closePatientDetailsModal}
                      disabled={isSavingPatientDetails}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="cn-case-detail-primary-button"
                      disabled={isSavingPatientDetails}
                    >
                      {isSavingPatientDetails ? "Saving..." : "Save details"}
                    </button>
                  </div>
                </form>
              </section>
            </div>
          ) : null}

          {isCloseCaseModalOpen ? (
            <div
              className="cn-case-detail-modal-backdrop"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  closeCloseCaseModal();
                }
              }}
            >
              <section
                className="cn-case-detail-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="cn-close-case-modal-title"
                tabIndex={-1}
                ref={closeDialogRef}
              >
                <div className="cn-case-detail-modal-header">
                  <div>
                    <h2 id="cn-close-case-modal-title">Close case</h2>
                    <p>
                      Close this Navigator case when no further Navigator action
                      is required.
                    </p>
                  </div>

                  <button
                    type="button"
                    className="cn-case-detail-modal-close"
                    onClick={closeCloseCaseModal}
                    disabled={isClosingCase}
                    aria-label="Close close-case dialog"
                  >
                    ×
                  </button>
                </div>

                {closeCaseError ? (
                  <div
                    className="cn-case-detail-modal-error"
                    role="alert"
                    tabIndex={-1}
                    ref={closeErrorRef}
                  >
                    {closeCaseError}
                  </div>
                ) : null}

                <form
                  className="cn-manual-provider-forward-form"
                  onSubmit={handleCloseCaseSubmit}
                >
                  <label>
                    <span>Closure reason</span>
                    <select
                      ref={closeReasonRef}
                      value={closeCaseReason}
                      onChange={(event) =>
                        setCloseCaseReason(event.target.value)
                      }
                      disabled={isClosingCase}
                      required
                    >
                      <option value="">Select a reason</option>
                      {closeCaseReasonOptions.map((reason) => (
                        <option key={reason} value={reason}>
                          {reason}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    <span>Internal note</span>
                    <textarea
                      ref={closeNoteRef}
                      value={closeCaseNote}
                      onChange={(event) => setCloseCaseNote(event.target.value)}
                      maxLength={1000}
                      disabled={isClosingCase}
                      placeholder={
                        closeCaseReason === "Administrative closure"
                          ? "Required for Administrative closure"
                          : "Optional note"
                      }
                    />
                    <small>
                      Optional for most reasons. Required for Administrative
                      closure. Do not enter detailed medical history or
                      information that is not required for this workflow.
                    </small>
                  </label>

                  <div className="cn-case-detail-modal-actions">
                    <button
                      type="button"
                      className="cn-case-detail-secondary-button"
                      onClick={closeCloseCaseModal}
                      disabled={isClosingCase}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="cn-case-detail-primary-button"
                      disabled={isClosingCase}
                    >
                      {isClosingCase ? "Closing..." : "Close case"}
                    </button>
                  </div>
                </form>
              </section>
            </div>
          ) : null}

          {isIntakeLeaveDialogOpen ? (
            <div
              className="cn-case-detail-modal-backdrop"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  stayOnIntakeLeaveDialog();
                }
              }}
            >
              <section
                className="cn-case-detail-modal cn-intake-leave-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="cn-intake-leave-dialog-title"
                aria-describedby="cn-intake-leave-dialog-body"
                tabIndex={-1}
                ref={intakeLeaveDialogRef}
              >
                <div className="cn-case-detail-modal-header">
                  <div>
                    <h2 id="cn-intake-leave-dialog-title">
                      Unsaved intake changes
                    </h2>
                    <p id="cn-intake-leave-dialog-body">
                      You have changes in this Intake that have not been saved.
                      If you leave now, those changes will be lost.
                    </p>
                    <p>
                      To keep your work and return later, click Save Progress
                      before leaving.
                    </p>
                  </div>
                </div>

                <div className="cn-case-detail-modal-actions">
                  <button
                    type="button"
                    className="cn-case-detail-secondary-button"
                    onClick={confirmLeaveIntakeWithoutSaving}
                  >
                    Leave without saving
                  </button>
                  <button
                    type="button"
                    className="cn-case-detail-primary-button"
                    ref={intakeLeaveStayRef}
                    onClick={stayOnIntakeLeaveDialog}
                  >
                    Stay on Intake
                  </button>
                </div>
              </section>
            </div>
          ) : null}

          {isConsentRequiredDialogOpen ? (
            <div
              className="cn-case-detail-modal-backdrop"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  stayOnConsentRequiredDialog();
                }
              }}
            >
              <section
                className="cn-case-detail-modal cn-consent-required-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="cn-consent-required-dialog-title"
                aria-describedby="cn-consent-required-dialog-body"
                tabIndex={-1}
                ref={consentRequiredDialogRef}
              >
                <div className="cn-case-detail-modal-header">
                  <div>
                    <h2 id="cn-consent-required-dialog-title">
                      Consent required
                    </h2>
                    <p id="cn-consent-required-dialog-body">
                      Patient consent must be recorded before you can complete
                      Intake and forward this case to a provider.
                    </p>
                  </div>
                </div>

                <div className="cn-case-detail-modal-actions">
                  <button
                    type="button"
                    className="cn-case-detail-secondary-button"
                    onClick={stayOnConsentRequiredDialog}
                  >
                    Stay here
                  </button>
                  <button
                    type="button"
                    className="cn-case-detail-primary-button"
                    ref={consentRequiredGoToBasicsRef}
                    onClick={goToBasicsFromConsentRequiredDialog}
                  >
                    Go to Basics
                  </button>
                </div>
              </section>
            </div>
          ) : null}

          {isIntakeChangesSavedDialogOpen ? (
            <div
              className="cn-case-detail-modal-backdrop"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  continueEditingAfterIntakeChangesSaved();
                }
              }}
            >
              <section
                className="cn-case-detail-modal cn-intake-changes-saved-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="cn-intake-changes-saved-dialog-title"
                aria-describedby="cn-intake-changes-saved-dialog-body"
                tabIndex={-1}
                ref={intakeChangesSavedDialogRef}
              >
                <div className="cn-case-detail-modal-header">
                  <div>
                    <h2 id="cn-intake-changes-saved-dialog-title">
                      Intake changes saved
                    </h2>
                    <p id="cn-intake-changes-saved-dialog-body">
                      Your changes have been saved as a draft, but they have not
                      yet been applied to the completed Intake.
                    </p>
                    <p>
                      If you are finished making changes, complete Intake now so
                      the updated information can be used for matching and
                      forwarding.
                    </p>
                  </div>
                </div>

                <div className="cn-case-detail-modal-actions">
                  <button
                    type="button"
                    className="cn-case-detail-secondary-button"
                    onClick={continueEditingAfterIntakeChangesSaved}
                    disabled={isSavingIntake}
                  >
                    Continue editing
                  </button>
                  <button
                    type="button"
                    className="cn-case-detail-primary-button"
                    ref={intakeChangesSavedCompleteRef}
                    onClick={completeIntakeFromChangesSavedDialog}
                    disabled={isSavingIntake}
                  >
                    {isSavingIntake
                      ? "Completing intake..."
                      : "Complete Intake now"}
                  </button>
                </div>
              </section>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}